/**
 * Retry-able lazy component loader dla Vue Router.
 *
 * Po deploy nowych chunkow przegladarka moze prebowac pobrac stary hash
 * (cache index.html / SW). Pierwszy fetch fail'uje — retry z bust-cache
 * URL ma duza szanse powodzenia. Po 3 nieudanych probach forsujemy full
 * window.location.reload zeby pobrac nowy index.html z nowymi hashami.
 */

const RETRY_DELAYS = [200, 600, 1500] as const;

export function lazyRoute<T>(loader: () => Promise<T>): () => Promise<T> {
  return async function loadWithRetry() {
    for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt++) {
      try {
        return await loader();
      } catch (err) {
        const msg = (err as Error)?.message ?? '';
        const isChunkError =
          msg.includes('Failed to fetch dynamically imported module') ||
          msg.includes('Importing a module script failed') ||
          msg.includes('error loading dynamically imported module');

        if (!isChunkError || attempt === RETRY_DELAYS.length) {
          // Ostatnia szansa — full reload pobiera swiezy index.html
          if (isChunkError && typeof window !== 'undefined') {
            // Mark zeby uniknac petli — jesli juz przeladowywalismy, rzuc blad
            const marker = sessionStorage.getItem('moredeal.chunk-reload');
            const stamp = Date.now();
            if (!marker || stamp - Number(marker) > 30_000) {
              sessionStorage.setItem('moredeal.chunk-reload', String(stamp));
              window.location.reload();
              // Return a never-resolving promise — page is reloading
              return new Promise<T>(() => {});
            }
          }
          throw err;
        }

        await new Promise((r) => setTimeout(r, RETRY_DELAYS[attempt]));
      }
    }
    throw new Error('unreachable');
  };
}
